import { Injectable, signal } from '@angular/core';

/** Selectable reader text sizes, smallest first. `1` is the design default. */
export const TEXT_SCALE_STEPS = [0.9, 1, 1.15, 1.3] as const;

/** One of the sizes offered by the reader control. */
export type TextScale = (typeof TEXT_SCALE_STEPS)[number];

/** Size applied when nothing has been chosen or the stored value is unusable. */
export const DEFAULT_TEXT_SCALE: TextScale = 1;

/** `localStorage` key holding the reader preference across sessions. */
const STORAGE_KEY = 'st_text_scale';

/**
 * CSS custom property read by `html { zoom: var(--st-text-scale, 1) }` in
 * `_template1-skin.scss`.
 */
const CSS_VARIABLE = '--st-text-scale';

/**
 * Reader text sizing — lets a viewer scale the whole site up or down for
 * legibility, persisted per browser.
 *
 * Presentation only: the value is published as a CSS custom property and the
 * stylesheet does the rest, so no component needs to react to a change.
 */
@Injectable({ providedIn: 'root' })
export class TextScaleService {
  /** Active size. A signal so `OnPush` views update without a manual nudge. */
  readonly scale = signal<TextScale>(DEFAULT_TEXT_SCALE);

  /**
   * Applies the stored preference to the document.
   *
   * Called once from `AppComponent` rather than from the constructor: the
   * service is only instantiated on first injection, and the rail that offers
   * the control is absent on header-less routes, so construction alone is not
   * a reliable moment to restore the setting.
   */
  restore(): void {
    this.apply(this.readStored());
  }

  /**
   * Selects a size and persists it.
   *
   * @param scale Size to apply; ignored if it is not one of {@link TEXT_SCALE_STEPS}.
   */
  set(scale: TextScale): void {
    if (!TEXT_SCALE_STEPS.includes(scale)) {
      return;
    }
    this.apply(scale);
    this.persist(scale);
  }

  /** Steps up one size, stopping at the largest. */
  increase(): void {
    this.step(1);
  }

  /** Steps down one size, stopping at the smallest. */
  decrease(): void {
    this.step(-1);
  }

  /** Returns to the design default. */
  reset(): void {
    this.set(DEFAULT_TEXT_SCALE);
  }

  /** True when the current size is the design default — drives the reset state. */
  get isDefault(): boolean {
    return this.scale() === DEFAULT_TEXT_SCALE;
  }

  /**
   * Moves the selection by one position in {@link TEXT_SCALE_STEPS}.
   *
   * @param delta `+1` for larger, `-1` for smaller.
   */
  private step(delta: number): void {
    const next = TEXT_SCALE_STEPS.indexOf(this.scale()) + delta;
    // Clamp rather than wrap: reaching the end of the range should feel like a
    // limit, not silently jump back to the opposite extreme.
    const clamped = Math.min(Math.max(next, 0), TEXT_SCALE_STEPS.length - 1);
    this.set(TEXT_SCALE_STEPS[clamped]);
  }

  /** Publishes the size to the document root, where the stylesheet reads it. */
  private apply(scale: TextScale): void {
    this.scale.set(scale);
    document.documentElement.style.setProperty(CSS_VARIABLE, String(scale));
  }

  /**
   * Reads the stored preference.
   *
   * @returns The saved size, or {@link DEFAULT_TEXT_SCALE} when absent, invalid,
   *   or when storage is unavailable (private mode, blocked site data).
   */
  private readStored(): TextScale {
    try {
      const stored = Number(localStorage.getItem(STORAGE_KEY));
      return this.toTextScale(stored);
    } catch {
      return DEFAULT_TEXT_SCALE;
    }
  }

  /**
   * Narrows an untrusted number to a supported size.
   *
   * @param value Raw value read from storage.
   * @returns The matching size, else {@link DEFAULT_TEXT_SCALE}.
   */
  private toTextScale(value: number): TextScale {
    const match = TEXT_SCALE_STEPS.find((step) => step === value);
    return match ?? DEFAULT_TEXT_SCALE;
  }

  /**
   * Saves the preference, ignoring storage failures.
   *
   * @param scale Size to store.
   */
  private persist(scale: TextScale): void {
    try {
      localStorage.setItem(STORAGE_KEY, String(scale));
    } catch {
      // Storage is a convenience here — a blocked write must not break the
      // control, the size is already applied to the document.
    }
  }
}
